import React, { useState } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  setPersistence, 
  browserLocalPersistence, 
  browserSessionPersistence,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth, db } from '../lib/firebase';
import { doc, setDoc, getDoc, updateDoc, getDocs, query, collection, where } from 'firebase/firestore';
import { createFirstCompany } from '../lib/invitations'; // Import correct en haut du fichier
import { CheckCircle2, ArrowRight, Loader2, AlertCircle, CheckSquare } from 'lucide-react';
import Button from './ui/Button';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  
  // Récupérer le token d'invitation depuis l'URL
  const queryParams = new URLSearchParams(window.location.search);
  const inviteToken = queryParams.get('token');
  const inviterId = queryParams.get('team');

  // Si on arrive avec une invitation, on bascule auto sur l'inscription
  React.useEffect(() => {
      if (inviteToken) setIsLogin(false);
  }, [inviteToken]);

  // --- HELPER : GESTION DE L'INSCRIPTION / INVITATION ---
  const handlePostLogin = async (user) => {
      // Vérifier si c'est une invitation
      if (inviteToken && inviterId) {
          const q = query(collection(db, 'artifacts', 'batiflow-app', 'users', inviterId, 'team'), where('token', '==', inviteToken));
          const snapshot = await getDocs(q);
          
          if (!snapshot.empty) {
              const inviteDoc = snapshot.docs[0];
              const inviteData = inviteDoc.data();
              
              // On met à jour l'invitation
              await updateDoc(doc(db, 'artifacts', 'batiflow-app', 'users', inviterId, 'team', inviteDoc.id), {
                  status: 'active',
                  uid: user.uid,
                  joinedAt: new Date().toISOString()
              });
              
              // On crée le profil utilisateur lié
              await setDoc(doc(db, 'users', user.uid), {
                  email: user.email,
                  companyName: "Membre Équipe",
                  role: inviteData.role,
                  subscriptionStatus: 'active', // Payé par le patron
                  teamId: inviterId,
                  createdAt: new Date().toISOString()
              }, { merge: true }); // Merge pour ne pas écraser si le compte existait
              return;
          }
      }

      // Si ce n'est pas une invitation, on vérifie si le profil existe déjà
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
          // Définir le nom : soit celui du formulaire, soit le nom Google, soit une valeur par défaut
          const finalCompanyName = companyName || user.displayName || 'Ma Société';

          // 1. Création du profil utilisateur de base
          await setDoc(doc(db, 'users', user.uid), {
              email: user.email,
              companyName: finalCompanyName,
              role: 'owner',
              subscriptionStatus: 'inactive',
              createdAt: new Date().toISOString()
          });

          // 2. CORRECTION : Appel explicite pour créer l'entreprise structurellement
          try {
             // Cela va appeler la Cloud Function qui crée l'entreprise et met à jour le champ companyIds du user
             await createFirstCompany(user.uid, finalCompanyName);
          } catch (err) {
             console.error("Erreur critique lors de la création auto de l'entreprise:", err);
             // On ne bloque pas l'utilisateur, mais il devra peut-être la recréer manuellement si ça échoue
          }
      }
  };

  // --- CONNEXION EMAIL ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);

      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await handlePostLogin(userCredential.user);
      }
    } catch (err) {
      console.error("Erreur Auth:", err);
      if (err.code === 'auth/invalid-email') setError("L'adresse email n'est pas valide.");
      else if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') setError("Email ou mot de passe incorrect.");
      else if (err.code === 'auth/email-already-in-use') setError("Cet email possède déjà un compte.");
      else if (err.code === 'auth/weak-password') setError("Le mot de passe doit faire au moins 6 caractères.");
      else setError("Erreur de connexion.");
    }
    setLoading(false);
  };

  // --- CONNEXION GOOGLE ---
  const handleGoogleLogin = async () => {
      setLoading(true);
      setError('');
      try {
          await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
          const provider = new GoogleAuthProvider();
          const result = await signInWithPopup(auth, provider);
          await handlePostLogin(result.user);
      } catch (err) {
          console.error("Erreur Google:", err);
          if (err.code === 'auth/popup-closed-by-user') setError("Connexion annulée.");
          else setError("Erreur de connexion Google.");
      }
      setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">
      {/* Colonne Gauche (Visuel) */}
      <div className="md:w-1/2 bg-slate-900 text-white p-8 md:p-12 flex flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20 z-0"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center font-bold text-xl">B</div>
            <span className="font-bold text-xl tracking-wide">BatiFlow</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
            {inviteToken ? "Rejoignez l'équipe !" : <>Gérez vos chantiers comme un <span className="text-orange-500">Pro</span>.</>}
          </h1>
          <p className="text-slate-400 text-lg mb-8">
             {inviteToken ? "Connectez-vous pour accéder aux chantiers partagés." : "Devis, Factures, Planning et Suivi de chantier. Tout-en-un."}
          </p>
          {!inviteToken && (
            <div className="space-y-4 hidden md:block">
                {['Devis ultra-rapides', 'Factures conformes', 'Suivi de chantier', 'Signature électronique'].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                    <div className="bg-green-500/20 p-1 rounded-full"><CheckCircle2 size={16} className="text-green-400"/></div>
                    <span className="font-medium">{item}</span>
                </div>
                ))}
            </div>
          )}
        </div>
        <div className="relative z-10 text-xs text-slate-500 mt-8">
          © 2025 BatiFlow - Logiciel d'Artisan
        </div>
      </div>

      {/* Colonne Droite (Formulaire) */}
      <div className="md:w-1/2 flex items-center justify-center p-6 animate-page bg-white">
        <div className="w-full max-w-md">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">
              {inviteToken ? 'Accepter l\'invitation' : (isLogin ? 'Connexion' : 'Inscription Gratuite')}
          </h2>
          <p className="text-slate-500 mb-6">
              {inviteToken ? "Utilisez Google ou créez un compte." : (isLogin ? 'Accédez à votre espace artisan.' : 'Créez votre compte en quelques secondes.')}
          </p>

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg flex items-center gap-2">
              <AlertCircle size={16}/> {error}
            </div>
          )}

          {/* BOUTONS SOCIAUX */}
          <div className="grid grid-cols-2 gap-3 mb-6">
              <button 
                onClick={handleGoogleLogin}
                className="flex items-center justify-center gap-2 py-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 transition font-medium text-sm text-slate-700"
              >
                  <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Google
              </button>
              <button 
                onClick={() => alert("La connexion Apple nécessite un compte Développeur Apple payant et une validation de domaine.")}
                className="flex items-center justify-center gap-2 py-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 transition font-medium text-sm text-slate-700"
              >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.74 1.18 0 2.45-1.01 3.8-1.01 1.05.06 2 .51 2.65 1.19-4.88 2.06-2.46 8.17 2.18 9.68-.77 1.78-1.74 3.54-3.71 2.37zM13 6.19c.57-2.17 3.16-3.07 2.91.08-.07 2.09-2.52 3.34-2.91-.08z"/></svg>
                  Apple
              </button>
          </div>

          <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-400 font-bold">Ou avec email</span></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && !inviteToken && (
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase mb-1 block ml-1">Nom de l'entreprise</label>
                <input 
                  type="text" 
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 transition"
                  placeholder="Ex: Durand Rénovation"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                />
              </div>
            )}
            
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase mb-1 block ml-1">Email</label>
              <input 
                type="text" 
                inputMode="email"
                autoComplete="email"
                required 
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 transition"
                placeholder="votre.nom@gmail.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase mb-1 block ml-1">Mot de passe</label>
              <input 
                type="password" 
                required 
                autoComplete={isLogin ? "current-password" : "new-password"}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 transition"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>

            <div 
                className="flex items-center gap-2 cursor-pointer w-fit group"
                onClick={() => setRememberMe(!rememberMe)}
            >
                <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${rememberMe ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300 group-hover:border-blue-400'}`}>
                    {rememberMe && <CheckSquare size={14} className="text-white" />}
                </div>
                <span className="text-sm text-slate-600 group-hover:text-slate-800 select-none">Rester connecté</span>
            </div>

            <Button variant="primary" className="w-full justify-center py-3 mt-6 shadow-lg shadow-blue-900/20" disabled={loading}>
              {loading ? <Loader2 className="animate-spin"/> : (inviteToken ? "Rejoindre l'équipe" : (isLogin ? 'Se connecter' : 'Créer mon compte'))}
              {!loading && <ArrowRight size={18}/>}
            </Button>
          </form>

          {!inviteToken && (
              <div className="mt-8 text-center text-sm text-slate-500">
                {isLogin ? "Pas encore de compte ?" : "Déjà inscrit ?"}
                <button 
                  onClick={() => { setIsLogin(!isLogin); setError(''); }}
                  className="text-blue-600 font-bold ml-2 hover:underline"
                >
                  {isLogin ? "S'inscrire" : "Se connecter"}
                </button>
              </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;